import { memo, useRef, useState } from 'react';
import { COACH_MARKETPLACE_PAGE, MY_ACTIVITY_PAGE, getEventUrl } from 'constants/pages';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { getEventLocation } from 'utils/shared/location/getEventLocation';
import { useModal } from 'hooks/useModal';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import ModalShare from 'screens/LessonPage/ModalShare';
import { UpcomingLessonType, UpcomingLessonsProps } from 'screens/PlayerLoggedInScreen/props';
import { ButtonLink } from 'components/Button';
import CardsSection from 'components/CardSection';
import TournamentCard from 'components/Explore/Tournaments/TournamentCard';
import { formatDateRange } from 'components/Explore/Tournaments/utils';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';
import LessonCancelRequest from './LessonCancelRequest';
import UpcomingLessonCard from './UpcomingLessonCard';
import UpcomingLessonDetailModal from './UpcomingLessonDetailModal';
import EmptyState from '../EmptyState';
import LatestRecapCard from '../LatestRecapCard';

const MemoizedTournamentCard = memo(TournamentCard);

type UpcomingItem =
  | { kind: 'lesson'; data: UpcomingLessonType; startTimestamp: number }
  | {
      kind: 'event';
      data: ReturnType<typeof buildDecoratedEvent>;
      startTimestamp: number;
    };

function buildDecoratedEvent(
  event: UpcomingLessonsProps['participatedEvents'][0],
  position: UpcomingLessonsProps['position'],
) {
  const distance =
    position && event.latitude != null && event.longitude != null
      ? calculateHaversineDistance({
          coord1: { latitude: position.latitude, longitude: position.longitude },
          coord2: { latitude: event.latitude, longitude: event.longitude },
          unit: 'miles',
        })
      : 0;

  return {
    ...event,
    startTimestamp: new Date(event.startDateTime).getTime(),
    displayDate: formatDateRange(
      new Date(event.startDateTime),
      new Date(event.endDateTime),
      resolveVenueTimeZone(event),
    ),
    distance: Math.round(distance),
  };
}

const UpcomingLessons: React.FC<UpcomingLessonsProps> = ({
  lessons,
  loading,
  participatedEvents,
  participatedEventsLoading,
  position,
  latestRecap,
  refetchUpcomingLessons,
  handleBookLessonWithCoach,
}) => {
  const prevArrowRef = useRef<HTMLDivElement>(null);
  const nextArrowRef = useRef<HTMLDivElement>(null);
  const [listHoveredTournamentId, setListHoveredTournamentId] = useState<string | null>(null);

  const playerCancelPromptModal = useModal();
  const shareLessonModal = useModal();
  const upcommingLessonDetailModal = useModal();

  const [selectedLesson, setSelectedLesson] = useState<UpcomingLessonType | null>(null);

  const mergedItems: UpcomingItem[] = [
    ...lessons.map(
      (lesson): UpcomingItem => ({
        kind: 'lesson',
        data: lesson,
        startTimestamp: lesson.startTimestamp,
      }),
    ),
    ...participatedEvents.map(
      (event): UpcomingItem => ({
        kind: 'event',
        data: buildDecoratedEvent(event, position),
        startTimestamp: new Date(event.startDateTime).getTime(),
      }),
    ),
  ].sort((a, b) => a.startTimestamp - b.startTimestamp);

  const getArrow = (type: 'prev' | 'next') => {
    if (type === 'prev') {
      return (
        <div
          ref={prevArrowRef}
          className="grid h-10 w-10 place-items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:bg-color-bg-lightmode-primary"
        >
          <ChevronLeft className="h-4 w-4" />
        </div>
      );
    } else {
      return (
        <div
          ref={nextArrowRef}
          className="grid h-10 w-10 place-items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:bg-color-bg-lightmode-primary"
        >
          <ChevronRight className="h-4 w-4" />
        </div>
      );
    }
  };

  const handleLessonClick = (lesson: UpcomingLessonType) => {
    setSelectedLesson(lesson);
    upcommingLessonDetailModal.openModal();
  };

  const onCancelLesson = () => {
    if (selectedLesson) {
      upcommingLessonDetailModal.closeModal();
      playerCancelPromptModal.openModal();
    }
  };

  const onShareLesson = () => {
    upcommingLessonDetailModal.closeModal();
    shareLessonModal.openModal();
  };

  const isLoading = loading || participatedEventsLoading;
  const isEmpty = !isLoading && mergedItems.length === 0;

  return (
    <>
      <div
        className={classNames(
          'container relative mx-auto mt-[1.6rem] border-b border-color-bg-input-lightmode-primary pb-ds-2xl dark:border-color-bg-input-darkmode-primary max-sm:pl-4',
          mergedItems.length > 0 ? 'max-sm:pl-4' : 'max-sm:px-4',
        )}
      >
        {latestRecap && (
          <LatestRecapCard
            recap={latestRecap}
            handleBookAnotherLesson={handleBookLessonWithCoach}
          />
        )}
        <div className="mb-5 flex items-start justify-between gap-2 max-sm:mb-3 max-sm:items-center">
          <div className="flex items-center gap-3">
            <h3 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
              Upcoming
            </h3>
            <Link
              href={MY_ACTIVITY_PAGE}
              className="typography-product-button-label-small flex items-center text-color-text-brand"
            >
              View All Activity <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div
            className={classNames(
              'hidden items-center gap-2',
              mergedItems.length > 0 && 'sm:!flex',
            )}
          >
            {getArrow('prev')}
            {getArrow('next')}
          </div>

          <ButtonLink
            variant="brand"
            size="md"
            href={COACH_MARKETPLACE_PAGE}
            className={classNames(
              'h-fit w-fit max-sm:hidden',
              (mergedItems.length > 0 || isLoading) && 'hidden',
            )}
          >
            Book a Lesson
          </ButtonLink>
        </div>

        {isEmpty && <EmptyState text="You don't have any bookings yet. You cannot be serious!" />}

        <ButtonLink
          variant="brand"
          size="md"
          href={COACH_MARKETPLACE_PAGE}
          className={classNames(
            'mx-auto mt-ds-lg w-fit sm:hidden',
            (mergedItems.length > 0 || isLoading) && 'hidden',
          )}
        >
          Book a Lesson
        </ButtonLink>

        <CardsSection
          loading={isLoading}
          scrollOffset={{ base: 0, xs: 100, lg: 0 }}
          visibleCards={{ base: 1, xs: 1, md: 2, lg: 3 }}
          gap="1rem"
          externalPrevArrowRef={prevArrowRef}
          externalNextArrowRef={nextArrowRef}
          data={mergedItems}
          cardsWrapperClassName="py-4 px-3"
          renderCard={(item) => {
            if (item.kind === 'lesson') {
              return <UpcomingLessonCard lesson={item.data} onClick={handleLessonClick} />;
            }
            const tournament = item.data;
            return (
              <Link href={getEventUrl(tournament.slug)}>
                <MemoizedTournamentCard
                  listHoveredTournamentId={listHoveredTournamentId}
                  setListHoveredTournamentId={setListHoveredTournamentId}
                  tournamentId={tournament.id}
                  coverImageUrl={
                    tournament.coverImagePath
                      ? getImageUrl({
                          url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                          path: tournament.coverImagePath,
                        })
                      : ''
                  }
                  organizerImageUrl={
                    tournament.organizerImagePath
                      ? getImageUrl({
                          url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                          path: tournament.organizerImagePath,
                        })
                      : ''
                  }
                  title={tournament.title}
                  registrationClosedAt={tournament.registrationClosedAt}
                  startTimestamp={tournament.startTimestamp}
                  status={tournament.status}
                  sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                  sourceRegistrationCount={tournament.sourceRegistrationCount}
                  registrationCount={tournament.registrationsAggregate?.aggregate?.count || 0}
                  isExternal={tournament.isExternal}
                  displayDate={tournament.displayDate}
                  displayLocation={getEventLocation(tournament)}
                  distance={tournament.distance}
                  showImageAndPeople={true}
                />
              </Link>
            );
          }}
        />
      </div>
      <UpcomingLessonDetailModal
        open={upcommingLessonDetailModal.isOpen}
        handleClose={upcommingLessonDetailModal.closeModal}
        lesson={selectedLesson as UpcomingLessonType}
        onCancel={onCancelLesson}
        onShare={onShareLesson}
      />
      <LessonCancelRequest
        lesson={selectedLesson}
        open={playerCancelPromptModal.isOpen}
        handleOpen={() => playerCancelPromptModal.openModal()}
        handleClose={() => playerCancelPromptModal.closeModal()}
        handleCancelSuccess={refetchUpcomingLessons}
      />
      <ModalShare
        lessonId={selectedLesson!?.id}
        isOpen={shareLessonModal.isOpen}
        setIsOpen={shareLessonModal.setIsOpen}
      />
    </>
  );
};

export default UpcomingLessons;
